/**
 * This is more or less a straight copy of a component from googles new official react integration (@vis.gl/react-google-maps).
 * The plan is to eventually use this new integration and use the component from there directly.
 * @see https://github.com/visgl/react-google-maps/blob/3dcce57975730757a550e7d9926dd3a4c9d38d76/src/components/advanced-marker.tsx
 * @see https://cloud.google.com/blog/products/maps-platform/introducing-react-components-for-the-maps-javascript-api/?hl=en
 */
import type {Ref, PropsWithChildren} from 'react';

import {MapContext} from '@react-google-maps/api';
import {
  Children,
  forwardRef,
  useContext,
  useEffect,
  useImperativeHandle,
  useState,
} from 'react';
import {createPortal} from 'react-dom';

type AdvancedMarkerEventProps = {
  onClick?: (event: google.maps.MapMouseEvent) => void;
  onDrag?: (event: google.maps.MapMouseEvent) => void;
  onDragStart?: (event: google.maps.MapMouseEvent) => void;
  onDragEnd?: (event: google.maps.MapMouseEvent) => void;
};

type AdvancedMarkerProps = PropsWithChildren<
  Omit<google.maps.marker.AdvancedMarkerElementOptions, 'gmpDraggable'> &
    AdvancedMarkerEventProps & {
      /**
       * className to add a class to the advanced marker element
       * Can only be used with HTML Marker content
       */
      className?: string;
      draggable?: boolean;
    }
>;

type AdvancedMarkerRef = google.maps.marker.AdvancedMarkerElement | null;

const useAdvancedMarker = (props: AdvancedMarkerProps) => {
  const [marker, setMarker] =
    useState<google.maps.marker.AdvancedMarkerElement | null>(null);
  const [contentContainer, setContentContainer] =
    useState<HTMLDivElement | null>(null);

  const map = useContext(MapContext);

  const {
    children,
    className,
    onClick,
    onDrag,
    onDragStart,
    onDragEnd,
    collisionBehavior,
    draggable,
    position,
    title,
    zIndex,
  } = props;

  const numberChilds = Children.count(children);

  // create marker instance and add it to the map when map becomes available
  useEffect(() => {
    if (!map) {
      return undefined;
    }

    const newMarker = new window.google.maps.marker.AdvancedMarkerElement();
    newMarker.map = map;

    setMarker(newMarker);

    // create container for marker content if there are children
    if (numberChilds > 0) {
      const element = document.createElement('div');
      if (className) {
        element.classList.add(className);
      }

      newMarker.content = element;

      setContentContainer(element);
    }

    return () => {
      newMarker.map = null;
      setMarker(null);
      setContentContainer(null);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- todo
  }, [map, numberChilds]);

  // bind all marker events
  useEffect(() => {
    if (!marker) {
      return;
    }

    const m = marker;

    if (onClick) {
      marker.addListener('click', onClick);
    }
    if (onDrag) {
      marker.addListener('drag', onDrag);
    }
    if (onDragStart) {
      marker.addListener('dragstart', onDragStart);
    }
    if (onDragEnd) {
      marker.addListener('dragend', onDragEnd);
    }

    if ((onDrag || onDragStart || onDragEnd) && !draggable) {
      // eslint-disable-next-line no-console -- warning should only appear in dev
      console.warn(
        'You need to set the marker to draggable to listen to drag-events.'
      );
    }

    return () => {
      google.maps.event.clearInstanceListeners(m);
    };
  }, [marker, draggable, onClick, onDragStart, onDrag, onDragEnd]);

  // update other marker props when changed
  useEffect(() => {
    if (!marker) {
      return;
    }

    if (position !== undefined) {
      marker.position = position;
    }
    if (draggable !== undefined) {
      marker.gmpDraggable = draggable;
    }
    if (collisionBehavior !== undefined) {
      marker.collisionBehavior = collisionBehavior;
    }
    if (zIndex !== undefined) {
      marker.zIndex = zIndex;
    }
    if (typeof title === 'string') {
      marker.title = title;
    }
  }, [marker, position, draggable, collisionBehavior, zIndex, title]);

  return [marker, contentContainer] as const;
};

export const AdvancedMarker = forwardRef(function AdvancedMarkerComponent(
  props: AdvancedMarkerProps,
  ref: Ref<AdvancedMarkerRef>
) {
  const {children} = props;
  const [marker, contentContainer] = useAdvancedMarker(props);

  useImperativeHandle(ref, () => marker, [marker]);

  if (!marker || contentContainer === null) {
    return null;
  }

  return createPortal(children, contentContainer);
});
